
// ** Third Party Components
import {
  ArrowUpDown,
  ClipboardList,
  ClipboardX,
  FileCog,
  Grid,
  List,
  MailQuestion,
} from "lucide-react"

// ** UI Components
import { Badge } from "@components/common/badge"
import { Input } from "@components/common/input"
import { Label } from "@components/common/label"
import { Button } from "@components/common/button"
import { Separator } from "@components/common/separator"
import { RadioGroup, RadioGroupItem } from "@components/common/radio-group"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"

// ** Constants
import { SORTING_FIELDS, SORTING_ORDERS } from "@pma/utils/constants"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"
import { useFilesView } from "@pma/hooks/use-files-view"

// ** Custom Components
import FileFilter from "../../file-filter"
import UploadFile from "../../upload-file"
import AddSubFolder from "../../add-sub-folder"
import RestoreFolder from "../../restore-folder"
import DeleteFolder from "../../delete-folder"

export default function FilesToolbar() {
  const { translate } = useTranslator()
  const {
    activeFolder,
    rootFolder,
    folderItems,
    foldersMeta,
    breadcrumbs,
    search,
    setSearch,
    sortingObj,
    setSortingObj,
    fileTypes,
    setFileTypes,
    cutFiles,
    copyFiles,
    view,
    setView,
    isOrganizationOwner,
    isAdmin,
    setFolderItems,
    setFoldersMeta,
    setBreadcrumbs,
    setIsShow,
    setActiveFolder,
    setSelectedFiles,
    setShareViaEmailModal,
    setRecentDeletedFolder,
    handlePasteFiles,
    handlePateCopyFiles,
    handleClearCutFunctionality,
    handleClearCopyFunctionality,
    handleBatchSelection,
    handleTreeItemFolderClick,
    refetch,
    refetchAllFolder,
    pageParam,
    setPageParam,
    project,
  } = useFilesView()

  const totalItems = folderItems[activeFolder?.id]?.length ?? 0

  return (
    <div className="mx-2 flex flex-wrap justify-start gap-2">
      {/* Search */}
      <Input
        placeholder={`${translate("Search")}...`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8 w-full md:w-64 lg:w-96"
      />

      {/* File type filter */}
      <FileFilter
        files={folderItems[activeFolder?.id]}
        setFileTypes={setFileTypes}
        fileTypes={fileTypes}
      />

      {/* Sorting */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            size="sm"
            leftSection={<ArrowUpDown size={14} />}
          >
            {translate("Sorting By")}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-40">
          <div className="grid gap-4">
            <div className="space-y-2">
              {SORTING_FIELDS.map((field) => (
                <RadioGroup key={field.value} className="flex cursor-pointer items-center space-x-2">
                  <RadioGroupItem
                    value={sortingObj.sorting}
                    onClick={() => setSortingObj((prev) => ({ ...prev, sorting: field.value }))}
                    checked={sortingObj.sorting === field.value}
                    id={`field-${field.value}`}
                  />
                  <Label className="cursor-pointer text-sm" htmlFor={`field-${field.value}`}>
                    {translate(field.label)}
                  </Label>
                </RadioGroup>
              ))}
              <Separator className="my-2" />
              {SORTING_ORDERS.map((order) => (
                <RadioGroup key={order.value} className="flex cursor-pointer items-center space-x-2">
                  <RadioGroupItem
                    value={sortingObj.order}
                    onClick={() => setSortingObj((prev) => ({ ...prev, order: order.value }))}
                    checked={sortingObj.order === order.value}
                    id={`order-${order.value}`}
                  />
                  <Label className="cursor-pointer" htmlFor={`order-${order.value}`}>
                    {translate(order.label)}
                  </Label>
                </RadioGroup>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {activeFolder?.id && (
        <>
          {/* Paste / Clear clipboard */}
          {(cutFiles.length > 0 || copyFiles.length > 0) && (
            <>
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => {
                      if (cutFiles.length > 0) handlePasteFiles()
                      else if (copyFiles.length > 0) handlePateCopyFiles()
                    }}
                    disabled={
                      cutFiles.length > 0
                        ? !activeFolder?.id || cutFiles.some((f) => foldersMeta[f.id] === activeFolder.id)
                        : false
                    }
                    className="cursor-pointer"
                  >
                    <ClipboardList size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-secondary">
                  {translate("Paste")} {cutFiles.length > 0 ? cutFiles.length : copyFiles.length}{" "}
                  {(cutFiles.length || copyFiles.length) === 1 ? translate("file") : translate("files")}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => {
                      if (cutFiles.length > 0) handleClearCutFunctionality()
                      else if (copyFiles.length > 0) handleClearCopyFunctionality()
                    }}
                    className="flex items-center gap-2"
                  >
                    <ClipboardX size={16} />
                  </Button>
                  <TooltipContent className="bg-destructive">
                    {translate(cutFiles.length > 0 ? "Clear Cut" : "Clear Copy")}
                  </TooltipContent>
                </TooltipTrigger>
              </Tooltip>
            </>
          )}

          {/* Batch actions */}
          <Tooltip>
            <TooltipTrigger>
              <Button variant="default" size="icon" onClick={handleBatchSelection}>
                <FileCog size={16} />
              </Button>
              <TooltipContent>{translate("Batch actions")}</TooltipContent>
            </TooltipTrigger>
          </Tooltip>

          {/* Upload */}
          <UploadFile
            projectId={project?.id}
            revalidate={() => {
              if (pageParam === 1) refetch()
              else setPageParam(1)
            }}
            folder={activeFolder}
          />

          {/* Add subfolder */}
          <AddSubFolder parent={activeFolder} revalidate={refetch} type={0} refetchAllFolder={refetchAllFolder} />

          {/* Email folder */}
          <Tooltip>
            <TooltipTrigger>
              <Button
                variant="ghost"
                size="icon"
                className="bg-slate-200/50"
                onClick={() => {
                  setSelectedFiles([activeFolder])
                  setShareViaEmailModal(true)
                }}
              >
                <MailQuestion className="text-primary" size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-primary">
              {translate("Email folder content")}
            </TooltipContent>
          </Tooltip>

          {/* Restore deleted folder */}
          {activeFolder.id !== rootFolder.id &&
            activeFolder.deleted &&
            (isOrganizationOwner || isAdmin || activeFolder.deleted_by === (window as any).__authConfig?.id) && (
              <RestoreFolder
                setFolderItems={setFolderItems}
                revalidate={refetch}
                setActiveFolder={setActiveFolder}
                folder={activeFolder}
                refetchAllFolder={refetchAllFolder}
              />
            )}

          {/* Rename folder */}
          {activeFolder.id !== rootFolder.id && (
            <AddSubFolder
              parent={activeFolder}
              revalidate={refetch}
              type={1}
              folderItems={folderItems}
              setFolderItems={setFolderItems}
              setActiveFolder={setActiveFolder}
              setBreadcrumbs={setBreadcrumbs}
            />
          )}

          {/* Delete folder */}
          {activeFolder.id !== rootFolder.id && (
            <DeleteFolder
              folder={activeFolder}
              setFolderItems={setFolderItems}
              foldersMeta={foldersMeta}
              setIsShow={setIsShow}
              breadcrumbs={breadcrumbs}
              setBreadcrumbs={setBreadcrumbs}
              handleTreeItemFolderClick={handleTreeItemFolderClick}
              setFoldersMeta={setFoldersMeta}
              setRecentDeletedFolder={setRecentDeletedFolder}
              refetchAllFolder={refetchAllFolder}
            />
          )}

          {/* Item count */}
          <Badge variant="default" className="font-thin">
            {totalItems} {translate("Items")}
          </Badge>

          {/* View toggle */}
          <Tooltip>
            <TooltipTrigger>
              <Button variant="outline" size="icon" onClick={() => setView((p) => (p === 0 ? 1 : 0))}>
                {view === 0 ? <List size={16} /> : <Grid size={16} />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{translate(view === 0 ? "List View" : "Grid View")}</TooltipContent>
          </Tooltip>
        </>
      )}
    </div>
  )
}